<template>
  <div class="ltcs-request-page">
    <div class="page-header">
      <div class="title-area">
        <h2 class="page-title">롱텀케어 자료요청</h2>
        <p class="page-subtitle">롱텀케어 포털에서 자료를 자동으로 가져옵니다</p>
      </div>
      <!-- 자동 갱신 표시 -->
      <div v-if="hasActiveRequest" class="auto-refresh-badge">
        <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" />
        <span>자동 갱신 중</span>
      </div>
    </div>

    <div class="request-body">
      <!-- 요청 테이블 -->
      <div class="request-section">
        <div class="section-header">
          <h3>자료 요청</h3>
          <div class="month-selector">
            <v-select
              v-model="selectedYearMonth"
              :items="yearMonthOptions"
              label="조회 연월"
              variant="outlined"
              density="compact"
              hide-details
              style="width: 160px"
            />
          </div>
        </div>

        <div class="ltcs-params-preview">
          <div class="preview-title">
            <v-icon size="16" color="#1e3a5f">mdi-information-outline</v-icon>
            요청 시 전송되는 조건
          </div>
          <div class="preview-chips">
            <span class="preview-chip" :class="{ 'is-warn': !selectedYearMonth }">
              <em>조회 연월</em>
              <strong>{{ formatYearMonthDisplay(selectedYearMonth) }}</strong>
              <span v-if="selectedYearMonth" class="ym-code">{{ selectedYearMonth }}</span>
            </span>
            <span class="preview-chip" :class="{ 'is-warn': !bizGubun }">
              <em>사업구분</em>
              <strong>{{ bizGubun || '미선택' }}</strong>
            </span>
          </div>
          <p class="preview-hint">
            위 연월·사업구분 값으로 서버에 요청이 등록됩니다. 요청 버튼을 누르면 전송 전 한 번 더 확인합니다.
          </p>
        </div>

        <table class="request-table">
          <thead>
            <tr>
              <th style="width: 50px">No</th>
              <th>요청 유형</th>
              <th>설명</th>
              <th style="width: 120px">요청</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in requestTypes" :key="item.type">
              <td class="text-center">{{ index + 1 }}</td>
              <td>
                <div class="type-cell">
                  <v-icon :color="item.color" size="20" class="mr-2">{{ item.icon }}</v-icon>
                  <span class="type-name">{{ item.name }}</span>
                </div>
              </td>
              <td class="desc-cell">{{ item.description }}</td>
              <td class="action-cell">
                <v-btn
                  variant="flat"
                  size="small"
                  class="btn-register"
                  :loading="loadingMap[item.type]"
                  @click="submitRequest(item.type)"
                >
                  <v-icon size="16" class="mr-1">mdi-send</v-icon>
                  요청
                </v-btn>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 요청 내역 -->
      <div class="history-section">
        <div class="section-header">
          <h3>요청 내역</h3>
          <v-btn
            variant="text"
            size="small"
            @click="loadHistory"
            :loading="historyLoading"
          >
            <v-icon size="18">mdi-refresh</v-icon>
          </v-btn>
        </div>

        <v-data-table
          :headers="historyHeaders"
          :items="historyItems"
          :loading="historyLoading"
          class="history-table"
          density="compact"
          items-per-page="10"
        >
          <template #item.cmq_type="{ item }">
            <v-chip size="small" :color="getTypeColor(item.cmq_type)">
              {{ getTypeLabel(item.cmq_type) }}
            </v-chip>
          </template>

          <template #item.cmq_params="{ item }">
            <span class="params-cell">
              {{ getParamsLabel(item.cmq_params) }}
            </span>
          </template>

          <template #item.cmq_worker_id="{ item }">
            <span v-if="item.cmq_worker_id" class="params-cell">{{ item.cmq_worker_id }}</span>
            <span v-else>-</span>
          </template>

          <template #item.cmq_progress="{ item }">
            <span v-if="getLatestProgressMessage(item)" class="params-cell progress-msg">
              {{ getLatestProgressMessage(item) }}
            </span>
            <span v-else>-</span>
          </template>

          <template #item.cmq_status="{ item }">
            <div class="status-cell">
              <v-progress-circular
                v-if="item.cmq_status === 'processing'"
                indeterminate
                size="14"
                width="2"
                color="info"
                class="mr-1"
              />
              <v-chip size="small" :color="getStatusColor(item.cmq_status)">
                {{ getStatusLabel(item.cmq_status) }}
              </v-chip>
              <!-- 실패 시 에러 메시지 표시 -->
              <v-tooltip v-if="item.cmq_status === 'failed' && item.cmq_error_message" location="top">
                <template #activator="{ props }">
                  <v-icon 
                    v-bind="props" 
                    size="16" 
                    color="error" 
                    class="ml-1 error-icon"
                  >
                    mdi-alert-circle
                  </v-icon>
                </template>
                <span>{{ formatRequestError(item.cmq_error_message) }}</span>
              </v-tooltip>
            </div>
          </template>

          <template #item.cmq_requested_at="{ item }">
            {{ formatDateTime(item.cmq_requested_at) }}
          </template>

          <template #item.cmq_completed_at="{ item }">
            {{ item.cmq_completed_at ? formatDateTime(item.cmq_completed_at) : '-' }}
          </template>

          <template #item.actions="{ item }">
            <!-- 완료/실패 시 결과 보기 버튼 -->
            <v-btn
              v-if="item.cmq_status === 'completed' || item.cmq_status === 'failed'"
              icon
              variant="text"
              size="small"
              :color="item.cmq_status === 'completed' ? 'success' : 'error'"
              @click="showResultModal(item)"
              title="결과 보기"
            >
              <v-icon size="18">mdi-eye</v-icon>
            </v-btn>
            <!-- 대기/처리중일 때 취소 버튼 -->
            <v-btn
              v-if="item.cmq_status === 'pending' || item.cmq_status === 'processing'"
              icon
              variant="text"
              size="small"
              color="error"
              @click="cancelRequest(item)"
              title="요청 취소"
            >
              <v-icon size="18">mdi-close-circle</v-icon>
            </v-btn>
          </template>

          <template #no-data>
            <div class="no-data">
              <v-icon size="48" color="grey-lighten-2">mdi-clipboard-text-clock-outline</v-icon>
              <p>요청 내역이 없습니다</p>
            </div>
          </template>
        </v-data-table>
      </div>

      <!-- 매크로 워커 네트워크 -->
      <div class="worker-section">
        <div class="section-header">
          <h3>매크로 워커</h3>
          <v-btn variant="text" size="small" @click="loadWorkers" :loading="workersLoading">
            <v-icon size="18">mdi-refresh</v-icon>
          </v-btn>
        </div>
        <div v-if="workers.length === 0" class="worker-empty">
          <p>온라인 워커가 없습니다. PC에서 LtcRequest.exe 실행 후 「폴링 시작」을 눌러주세요.</p>
        </div>
        <div v-else class="worker-grid">
          <div
            v-for="w in workers"
            :key="w.cmw_worker_id"
            class="worker-card"
            :class="{ offline: w.online_status === 'offline' }"
          >
            <div class="worker-card-header">
              <span class="worker-id">{{ w.cmw_worker_id }}</span>
              <v-chip size="x-small" :color="getWorkerStatusColor(w)">{{ w.status_label }}</v-chip>
            </div>
            <div class="worker-card-body">
              <span>{{ w.cmw_hostname }} · 슬롯 {{ w.cmw_instance_slot || 1 }}</span>
              <span v-if="w.cmw_task_fingerprint" class="task-fp">{{ w.cmw_task_fingerprint }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <LtcsRequestConfirmDialog
      v-model="confirmDialog.show"
      :type-name="confirmDialog.typeName"
      :year-month="confirmDialog.yearMonth"
      :biz-gubun="confirmDialog.bizGubun"
      :facility-name="facilityName"
      :needs-year-month="confirmDialog.needsYearMonth"
      :loading="!!loadingMap[confirmDialog.type]"
      @confirm="confirmSubmitRequest"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>

    <!-- 결과 보기 모달 (데이터 테이블) -->
    <v-dialog v-model="resultModal.show" max-width="2000" scrollable>
      <div class="result-modal result-modal--large">
        <div class="modal-header" :class="resultModal.status">
          <h3>
            <v-icon size="20" class="mr-2">
              {{ resultModal.status === 'completed' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
            </v-icon>
            {{ resultModal.status === 'completed' ? '실행 완료' : '실행 실패' }}
            <span v-if="resultModal.status === 'completed'" class="header-subtitle">
              - {{ getTypeLabel(resultModal.type) }}
            </span>
          </h3>
          <button class="close-btn" @click="closeResultModal">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>
        <div class="modal-body">
          <!-- 실패 원인 (실패 시) -->
          <div v-if="resultModal.status === 'failed' && resultModal.errorMessage" class="error-section">
            <div class="section-title">
              <v-icon size="16" color="error" class="mr-1">mdi-alert</v-icon>
              실패 원인
            </div>
            <div class="error-message">{{ resultModal.errorMessage }}</div>
          </div>

          <!-- 성공 시: 인건비지출내역 전용 표시 -->
          <div v-if="resultModal.status === 'completed' && resultModal.type === 'caion_ltcs_labor_cost'" class="labor-cost-section">
            <div v-if="scheduleData.items.length > 0" class="labor-cost-card">
              <div class="labor-cost-title-bar">
                ({{ formatYearMonth(scheduleData.items[0]?.yearMonth) }}) {{ scheduleData.items[0]?.facilityType || '-' }} 심사결정금액
              </div>
              <table class="labor-cost-table">
                <thead>
                  <tr class="header-row-1">
                    <th colspan="2">① 장기요양급여비용(②+③+④)</th>
                    <th colspan="2">② 인건비 비율 반영</th>
                    <th colspan="2">③ 인건비 비율 미반영</th>
                    <th>④ 본인부담금</th>
                  </tr>
                  <tr class="header-row-2">
                    <th>비용</th>
                    <th>가산</th>
                    <th>비용</th>
                    <th>가산</th>
                    <th>비용</th>
                    <th>가산</th>
                    <th>비용</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="amount">{{ formatNumber(scheduleData.items[0]?.totalCost) }}</td>
                    <td class="amount">{{ formatNumber(scheduleData.items[0]?.totalAdd) }}</td>
                    <td class="amount highlight">{{ formatNumber(scheduleData.items[0]?.laborCost) }}</td>
                    <td class="amount highlight">{{ formatNumber(scheduleData.items[0]?.laborAdd) }}</td>
                    <td class="amount">{{ formatNumber(scheduleData.items[0]?.nonLaborCost) }}</td>
                    <td class="amount">{{ formatNumber(scheduleData.items[0]?.nonLaborAdd) }}</td>
                    <td class="amount">{{ formatNumber(scheduleData.items[0]?.selfPayCost) }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="labor-cost-summary">
                <div class="summary-item">
                  <span class="label">인건비 반영 합계</span>
                  <span class="value">{{ formatNumber((scheduleData.items[0]?.laborCost || 0) + (scheduleData.items[0]?.laborAdd || 0)) }}원</span>
                </div>
                <button
                  v-if="duplicateCount > 0"
                  type="button"
                  class="duplicate-inline-btn"
                  @click="openDuplicateDialog"
                >
                  중복 {{ duplicateCount.toLocaleString() }}건
                </button>
              </div>
            </div>
            <div v-else class="no-data-message">
              <v-icon size="48" color="grey-lighten-2">mdi-file-document-outline</v-icon>
              <p>해당 조건의 인건비지출내역이 없습니다.</p>
            </div>
          </div>

          <!-- 성공 시: 일반 데이터 테이블 표시 -->
          <div v-if="resultModal.status === 'completed' && resultModal.type !== 'caion_ltcs_labor_cost'" class="data-table-section">
            <!-- 조회 조건 표시 -->
            <div class="query-conditions">
              <div class="condition-title">
                <v-icon size="16" class="mr-1">mdi-filter-outline</v-icon>
                조회 조건
              </div>
              <div class="condition-items">
                <div class="condition-item">
                  <span class="condition-label">사업구분</span>
                  <span class="condition-value">{{ bizGubun || '전체' }}</span>
                </div>
                <div class="condition-item">
                  <span class="condition-label">연월</span>
                  <span class="condition-value">{{ formatYearMonth(resultModal.yearMonth) || '전체' }}</span>
                </div>
                <div v-if="scheduleSearch" class="condition-item">
                  <span class="condition-label">검색어</span>
                  <span class="condition-value highlight">{{ scheduleSearch }}</span>
                </div>
              </div>
            </div>

            <!-- 요약 정보 -->
            <div class="summary-cards">
              <!-- 전체 건수 (공통) -->
              <div class="summary-card">
                <div class="card-icon">
                  <v-icon color="primary">mdi-calendar-check</v-icon>
                </div>
                <div class="card-content">
                  <span class="card-value">{{ scheduleData.summary.totalCount?.toLocaleString() || 0 }}</span>
                  <span class="card-label">{{
                    resultModal.type === 'caion_ltcs_claim_detail' ? '방문 건수' : '전체 건수'
                  }}</span>
                </div>
              </div>
              <!-- 종사자 수 -->
              <div v-if="showCaregiverCount" class="summary-card">
                <div class="card-icon">
                  <v-icon color="success">mdi-account-hard-hat</v-icon>
                </div>
                <div class="card-content">
                  <span class="card-value">{{ scheduleData.summary.caregiverCount || 0 }}</span>
                  <span class="card-label">종사자</span>
                </div>
              </div>
              <!-- 수급자 수 -->
              <div v-if="showRecipientCount" class="summary-card">
                <div class="card-icon">
                  <v-icon color="secondary">mdi-account-heart</v-icon>
                </div>
                <div class="card-content">
                  <span class="card-value">{{ scheduleData.summary.recipientCount || 0 }}</span>
                  <span class="card-label">수급자</span>
                </div>
              </div>
              <!-- 날짜 수 -->
              <div v-if="showDateCount" class="summary-card">
                <div class="card-icon">
                  <v-icon color="info">mdi-calendar-range</v-icon>
                </div>
                <div class="card-content">
                  <span class="card-value">{{ scheduleData.summary.dateCount || 0 }}일</span>
                  <span class="card-label">날짜수</span>
                </div>
              </div>
              <!-- 급여종류 종류 -->
              <div v-if="resultModal.type === 'caion_ltcs_recipient_info'" class="summary-card">
                <div class="card-icon">
                  <v-icon color="info">mdi-star</v-icon>
                </div>
                <div class="card-content">
                  <span class="card-value">{{ scheduleData.summary.gradeCount || 0 }}</span>
                  <span class="card-label">급여종류</span>
                </div>
              </div>
              <!-- 총 수가 (청구내역상세) -->
              <div v-if="resultModal.type === 'caion_ltcs_claim_detail'" class="summary-card">
                <div class="card-icon">
                  <v-icon color="warning">mdi-currency-krw</v-icon>
                </div>
                <div class="card-content">
                  <span class="card-value">{{ (scheduleData.summary.totalFee || 0).toLocaleString() }}원</span>
                  <span class="card-label">총 수가</span>
                </div>
              </div>
              <div
                v-if="resultModal.type === 'caion_ltcs_claim_detail' && (scheduleData.summary.portalRawCount || 0) > (scheduleData.summary.totalCount || 0)"
                class="summary-card summary-card--portal-raw"
              >
                <div class="card-icon">
                  <v-icon color="#1565c0">mdi-file-document-multiple-outline</v-icon>
                </div>
                <div class="card-content">
                  <span class="card-value">{{ (scheduleData.summary.portalRawCount || 0).toLocaleString() }}줄</span>
                  <span class="card-label">포털 원본</span>
                </div>
              </div>
              <button
                v-if="duplicateCount > 0"
                type="button"
                class="summary-card summary-card--duplicate"
                @click="openDuplicateDialog"
              >
                <div class="card-icon">
                  <v-icon color="error">mdi-content-duplicate</v-icon>
                </div>
                <div class="card-content">
                  <span class="card-value duplicate-link">{{ duplicateCount.toLocaleString() }}건</span>
                  <span class="card-label">{{
                    resultModal.type === 'caion_ltcs_claim_detail'
                      ? '동일 방문 중복 (클릭)'
                      : '중복 데이터 (클릭)'
                  }}</span>
                </div>
              </button>
            </div>
            <LtcsClaimDetailPreferNotice
              v-if="resultModal.type === 'caion_ltcs_claim_detail'"
              :portal-raw-count="scheduleData.summary.portalRawCount || 0"
              :display-count="scheduleData.summary.totalCount || 0"
              :status-pair-count="scheduleData.summary.statusPairCount || 0"
            />

            <!-- 검색 영역 -->
            <div class="search-area">
              <v-text-field
                v-model="scheduleSearch"
                prepend-inner-icon="mdi-magnify"
                label="이름 검색 (요양보호사/수급자)"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                style="max-width: 300px"
                @keyup.enter="searchScheduleData"
                @click:clear="clearSearch"
              />
              <v-btn variant="flat" class="btn-search" @click="searchScheduleData">
                검색
              </v-btn>
              <v-spacer />
              <span class="total-count">
                총 <strong>{{ scheduleData.total.toLocaleString() }}</strong>건
                <template v-if="resultModal.type === 'caion_ltcs_claim_detail'"> (방문)</template>
              </span>
            </div>

            <!-- 데이터 테이블 -->
            <v-data-table
              :headers="scheduleHeaders"
              :items="scheduleData.items"
              :loading="scheduleLoading"
              :items-per-page="itemsPerPage"
              :page="currentPage"
              class="schedule-table"
              density="compact"
              :sort-by="sortBy"
              @update:sort-by="onSortChange"
              hide-default-footer
            >
              <!-- 날짜/시간/금액/YN 공통 포맷 -->
              <template #item.clsr_wdate="{ item }">{{ formatDateTime(item.clsr_wdate) }}</template>
              <template #item.clsr_rdate="{ item }">{{ formatDateTime(item.clsr_rdate) }}</template>
              <template #item.clsr_service_type="{ item }">
                <v-chip size="x-small" :color="getServiceTypeColor(item.clsr_service_type)">
                  {{ item.clsr_service_type || '-' }}
                </v-chip>
              </template>

              <template #item.clci_birth_date="{ item }">{{ formatDateFull(item.clci_birth_date) }}</template>
              <template #item.clci_work_start_date="{ item }">{{ formatDateFull(item.clci_work_start_date) }}</template>
              <template #item.clci_work_end_date="{ item }">{{ formatDateFull(item.clci_work_end_date) }}</template>
              <template #item.clci_wdate="{ item }">{{ formatDateTime(item.clci_wdate) }}</template>

              <template #item.clsp_date="{ item }">{{ formatDateFull(item.clsp_date) }}</template>
              <template #item.clsp_start_time="{ item }">{{ formatTime(item.clsp_start_time) }}</template>
              <template #item.clsp_end_time="{ item }">{{ formatTime(item.clsp_end_time) }}</template>
              <template #item.clsp_service_type="{ item }">
                <v-chip size="x-small" :color="getServiceTypeColor(item.clsp_service_type)">
                  {{ item.clsp_service_type || '-' }}
                </v-chip>
              </template>
              <template #item.clsp_is_family="{ item }">{{ formatYn(item.clsp_is_family) }}</template>
              <template #item.clsp_caregiver_birth="{ item }">{{ formatDateFull(item.clsp_caregiver_birth) }}</template>
              <template #item.clc_caregiver_birth="{ item }">{{ formatDateFull(item.clc_caregiver_birth) }}</template>

              <template #item.clc_date="{ item }">{{ formatDateFull(item.clc_date) }}</template>
              <template #item.clc_plan_time="{ item }">
                {{ formatTimeRange(item.clc_plan_start_time, item.clc_plan_end_time) }}
              </template>
              <template #item.clc_work_time="{ item }">
                {{ formatTimeRange(item.clc_work_start_time, item.clc_work_end_time) }}
              </template>
              <template #item.clc_is_family="{ item }">{{ formatYn(item.clc_is_family) }}</template>
              <template #item.clc_service_type="{ item }">
                <v-chip size="x-small" :color="getServiceTypeColor(item.clc_service_type)">
                  {{ item.clc_service_type || '-' }}
                </v-chip>
              </template>

              <template #item.clcd_status="{ item }">
                <v-chip size="x-small" :color="getClaimStatusColor(item.clcd_status)">
                  {{ item.clcd_status || '-' }}
                </v-chip>
              </template>
              <template #item.clcd_date="{ item }">{{ formatDateFull(item.clcd_date) }}</template>
              <template #item.clcd_service_time="{ item }">
                {{ formatTimeRange(item.clcd_service_start_time, item.clcd_service_end_time) }}
              </template>
              <template #item.clcd_fee="{ item }">{{ formatMoney(item.clcd_fee) }}</template>
              <template #item.clcd_deduction_score="{ item }">{{ item.clcd_deduction_score ?? '-' }}</template>
              <template #item.clcd_deduction_rate="{ item }">{{ formatRate(item.clcd_deduction_rate) }}</template>

              <template #no-data>
                <div class="no-data">
                  <v-icon size="48" color="grey-lighten-2">mdi-calendar-blank</v-icon>
                  <p>조회된 일정이 없습니다.</p>
                </div>
              </template>

              <template #loading>
                <div class="loading-indicator">
                  <v-progress-circular indeterminate color="primary" />
                  <p>데이터를 불러오는 중...</p>
                </div>
              </template>
            </v-data-table>

            <!-- 페이지네이션 -->
            <div class="pagination-area">
              <v-select
                v-model="itemsPerPage"
                :items="[20, 50, 100]"
                label="페이지당 표시"
                variant="outlined"
                density="compact"
                hide-details
                style="width: 120px"
                @update:model-value="onItemsPerPageChange"
              />
              <v-pagination
                v-model="currentPage"
                :length="totalPages"
                :total-visible="7"
                density="compact"
                @update:model-value="onPageChange"
              />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <v-btn variant="flat" class="btn-cancel" @click="closeResultModal">닫기</v-btn>
        </div>
      </div>
    </v-dialog>

    <LtcsDuplicateDialog
      v-model="duplicateDialogShow"
      :loading="duplicateLoading"
      :duplicate-count="duplicateCount"
      :duplicate-group-count="duplicateGroupCount"
      :groups="duplicateGroups"
      :table-headers="scheduleHeaders"
      :idx-key="duplicateIdxKey"
      :source-type="resultModal.type"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, inject, watch } from 'vue';
import api from '@/services/api';
import { useMemberYearMonthOptional } from '@/composables/useMemberYearMonth';
import LtcsDuplicateDialog from '@/components/member/request/LtcsDuplicateDialog.vue';
import LtcsClaimDetailPreferNotice from '@/components/member/request/LtcsClaimDetailPreferNotice.vue';
import LtcsRequestConfirmDialog from '@/components/member/request/LtcsRequestConfirmDialog.vue';
import type { LtcsDuplicateGroup } from '@/components/member/request/LtcsDuplicateDialog.vue';
import { getLtcsDuplicatesEndpoint, LTCS_TYPE_IDX_KEY } from '@/constants/ltcsMacro';
import { getLtcsResultHeaders } from '@/constants/ltcsResultHeaders';
import { formatUserFacingError } from '@/utils/formatUserFacingError';
import { CERT_EXPIRED_CRAWL_MESSAGE, hasNewCertExpiredFailure } from '@/utils/certExpiredCrawl';
import { useStore } from 'vuex';

const store = useStore();
const isAdminVerbose = computed(() => (store.getters.roleLevel || 0) >= 60);
const formatRequestError = (message: string) =>
  formatUserFacingError(message, {
    verbose: isAdminVerbose.value,
    fallback: '롱텀케어 접속에 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
  });

// 레이아웃에서 제공하는 데이터
const memberData = inject<any>('memberData');
const selectedBizGubun = inject<any>('selectedBizGubun');

const memberCode = computed(() => memberData?.value?.m_code || '');
const bizGubun = computed(() => selectedBizGubun?.value || '');
const facilityName = computed(
  () => memberData?.value?.m_name || memberData?.value?.m_facility_name || ''
);

const confirmDialog = ref({
  show: false,
  type: '',
  typeName: '',
  yearMonth: '',
  bizGubun: '',
  needsYearMonth: true,
});

const { yearMonth: sharedYearMonth, setYearMonth } = useMemberYearMonthOptional(memberCode);
const selectedYearMonth = ref('');
const historyLoading = ref(false);
const historyItems = ref<any[]>([]);
const loadingMap = ref<Record<string, boolean>>({});
const workers = ref<any[]>([]);
const workersLoading = ref(false);
let pollingInterval: ReturnType<typeof setInterval> | null = null;
let workerPollingInterval: ReturnType<typeof setInterval> | null = null;

const snackbar = ref({
  show: false,
  message: '',
  color: 'success'
});

// 결과 보기 모달
const resultModal = ref({
  show: false,
  status: '' as 'completed' | 'failed' | '',
  type: '',
  requestedAt: '',
  completedAt: '',
  result: null as any,
  errorMessage: '',
  yearMonth: '',
  bizGubun: '' // ★ 요청 시 사업구분 저장
});

// 일정계획 데이터 테이블 관련
const scheduleLoading = ref(false);
const scheduleSearch = ref('');
const scheduleData = ref({
  items: [] as any[],
  total: 0,
    summary: {
      totalCount: 0,
      caregiverCount: 0,
      gradeCount: 0,
      totalFee: 0,
      recipientCount: 0,
      dateCount: 0,
      duplicateCount: 0,
      duplicateGroupCount: 0,
      portalRawCount: 0,
      paidCount: 0,
      requestedCount: 0,
      statusPairCount: 0,
    }
});

const duplicateDialogShow = ref(false);
const duplicateLoading = ref(false);
const duplicateGroups = ref<LtcsDuplicateGroup[]>([]);
const duplicateCount = computed(() => Number(scheduleData.value.summary.duplicateCount) || 0);
const duplicateGroupCount = computed(() => Number(scheduleData.value.summary.duplicateGroupCount) || 0);
const duplicateIdxKey = computed(() => {
  const type = normalizeLtcsType(resultModal.value.type);
  return LTCS_TYPE_IDX_KEY[type] || 'idx';
});

const currentPage = ref(1);
const itemsPerPage = ref(50);
const sortBy = ref([{ key: 'clsp_date', order: 'asc' as const }]);

const totalPages = computed(() => Math.ceil(scheduleData.value.total / itemsPerPage.value));

// 현재 타입에 따른 헤더 (ltcsResultHeaders 단일 소스)
const scheduleHeaders = computed(() =>
  getLtcsResultHeaders(normalizeLtcsType(resultModal.value.type))
);

const showCaregiverCount = computed(() => {
  const types = ['caion_ltcs_emp_info', 'caion_ltcs_schedule_plan', 'caion_ltcs_claim', 'caion_ltcs_claim_detail'];
  return types.includes(resultModal.value.type);
});

const showRecipientCount = computed(() => {
  const types = ['caion_ltcs_recipient_info', 'caion_ltcs_schedule_plan', 'caion_ltcs_claim', 'caion_ltcs_claim_detail'];
  return types.includes(resultModal.value.type);
});

const showDateCount = computed(() => {
  const types = ['caion_ltcs_schedule_plan', 'caion_ltcs_claim'];
  return types.includes(resultModal.value.type);
});

/** DB에 남아 있을 수 있는 구 cmq_type → 현재 타입 (조회·표시용) */
const normalizeLtcsType = (type: string) => {
  if (type === 'caion_ltcs_caregiver_plan') return 'caion_ltcs_schedule_plan';
  if (type === 'caion_ltcs_caregiver_info') return 'caion_ltcs_emp_info';
  return type;
};

const TYPE_LABELS: Record<string, { name: string; color: string; icon: string; description: string; needsYearMonth: boolean }> = {
  caion_ltcs_recipient_info: {
    name: '수급자 정보',
    description: '롱텀케어에서 수급자 정보를 가져옵니다.',
    icon: 'mdi-account-heart',
    color: 'secondary',
    needsYearMonth: false
  },
  caion_ltcs_emp_info: {
    name: '요양보호사 정보',
    description: '롱텀케어에서 요양보호사 정보를 가져옵니다.',
    icon: 'mdi-badge-account',
    color: 'success',
    needsYearMonth: false
  },
  caion_ltcs_schedule_plan: {
    name: '요양보호사 일정계획',
    description: '롱텀케어에서 요양보호사 일정계획을 가져옵니다.',
    icon: 'mdi-calendar-clock',
    color: 'info',
    needsYearMonth: true
  },
  caion_ltcs_rfid: {
    name: 'RFID',
    description: '롱텀케어에서 RFID 전송내용을 가져옵니다.',
    icon: 'mdi-nfc-variant',
    color: 'info',
    needsYearMonth: true
  },
  caion_ltcs_claim: {
    name: '청구내역',
    description: '롱텀케어에서 청구내역을 가져옵니다.',
    icon: 'mdi-file-document-outline',
    color: 'primary',
    needsYearMonth: true
  },
  caion_ltcs_claim_detail: {
    name: '청구내역상세',
    description: '롱텀케어에서 청구내역 상세를 가져옵니다.',
    icon: 'mdi-file-document-multiple',
    color: 'secondary',
    needsYearMonth: true
  },
  caion_ltcs_labor_cost: {
    name: '인건비지출내역',
    description: '롱텀케어에서 종사자인건비 지출내역을 가져옵니다.',
    icon: 'mdi-cash-multiple',
    color: 'success',
    needsYearMonth: true
  }
};

const requestTypes = Object.entries(TYPE_LABELS).map(([type, meta]) => ({
  type,
  ...meta
}));

const historyHeaders = [
  { title: '요청 유형', key: 'cmq_type', sortable: false },
  { title: '조회 조건', key: 'cmq_params', sortable: false },
  { title: '상태', key: 'cmq_status', sortable: false },
  { title: '진행', key: 'cmq_progress', sortable: false },
  { title: '워커', key: 'cmq_worker_id', sortable: false },
  { title: '요청일시', key: 'cmq_requested_at', sortable: true },
  { title: '완료일시', key: 'cmq_completed_at', sortable: true },
  { title: '', key: 'actions', sortable: false, width: 60 }
];

// 대기/처리중인 요청이 있는지 확인
const hasActiveRequest = computed(() => {
  return historyItems.value.some(
    item => item.cmq_status === 'pending' || item.cmq_status === 'processing'
  );
});

const yearMonthOptions = computed(() => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
    const title = `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
    options.push({ title, value });
  }
  return options;
});

const getTypeLabel = (type: string) => {
  return TYPE_LABELS[normalizeLtcsType(type)]?.name || type;
};

const getTypeColor = (type: string) => {
  return TYPE_LABELS[normalizeLtcsType(type)]?.color || 'grey';
};

const needsYearMonth = (type: string) => TYPE_LABELS[normalizeLtcsType(type)]?.needsYearMonth ?? true;

const getLatestProgressMessage = (item: any) => {
  const progress = item.cmq_progress;
  if (!progress) return '';
  try {
    const list = Array.isArray(progress) ? progress : (typeof progress === 'string' ? JSON.parse(progress) : []);
    const last = list[list.length - 1];
    return last?.message || last?.status || '';
  } catch {
    return '';
  }
};

const getWorkerStatusColor = (w: any) => {
  if (w.online_status === 'offline') return 'grey';
  if (w.status_label === '처리중') return 'info';
  if (w.status_label === '폴링 대기') return 'success';
  return 'warning';
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'pending': return '대기중';
    case 'processing': return '처리중';
    case 'completed': return '완료';
    case 'failed': return '실패';
    case 'cancelled': return '취소됨';
    default: return status;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending': return 'warning';
    case 'processing': return 'info';
    case 'completed': return 'success';
    case 'failed': return 'error';
    case 'cancelled': return 'grey';
    default: return 'grey';
  }
};

const formatYearMonthDisplay = (ym: string) => {
  if (!ym || ym.length !== 6) return ym || '미선택';
  return `${ym.substring(0, 4)}년 ${parseInt(ym.substring(4, 6), 10)}월`;
};

const getParamsLabel = (params: any) => {
  if (!params) return '-';
  try {
    const p = typeof params === 'string' ? JSON.parse(params) : params;
    const ym = String(p.yearMonth || '');
    const biz = p.bizGubun || '';
    if (!ym && !biz) return '연월 없음';
    if (ym.length === 6) {
      const label = `${formatYearMonthDisplay(ym)} (${ym})`;
      return biz ? `${label} / ${biz}` : label;
    }
    return biz ? `${ym} / ${biz}` : ym;
  } catch {
    return '-';
  }
};

const formatDateTime = (date: string) => {
  if (!date) return '-';
  return new Date(date).toLocaleString('ko-KR');
};

const formatNumber = (num: number | undefined | null) => {
  if (num === undefined || num === null) return '0';
  return num.toLocaleString('ko-KR');
};

const showMessage = (message: string, color = 'success') => {
  snackbar.value = { show: true, message, color };
};

const submitRequest = async (type: string) => {
  if (needsYearMonth(type)) {
    if (!selectedYearMonth.value) {
      showMessage('조회 연월을 선택해주세요.', 'error');
      return;
    }
    if (!bizGubun.value) {
      showMessage('사업구분이 선택되지 않았습니다.', 'error');
      return;
    }
  }
  if (!memberCode.value) {
    showMessage(
      isAdminVerbose.value
        ? '기관 정보(m_code)를 불러올 수 없습니다.'
        : '기관 정보를 불러올 수 없습니다.',
      'error'
    );
    return;
  }

  confirmDialog.value = {
    show: true,
    type,
    typeName: getTypeLabel(type),
    yearMonth: needsYearMonth(type) ? selectedYearMonth.value : '',
    bizGubun: needsYearMonth(type) ? bizGubun.value : '',
    needsYearMonth: needsYearMonth(type),
  };
};

const confirmSubmitRequest = async () => {
  const type = confirmDialog.value.type;
  if (!type) return;

  loadingMap.value[type] = true;
  try {
    const params: Record<string, string> = { m_code: memberCode.value };
    if (needsYearMonth(type)) {
      params.yearMonth = selectedYearMonth.value;
      params.bizGubun = bizGubun.value;
    }

    await api.post('/macro/request', {
      m_code: memberCode.value,
      cmq_type: type,
      params
    });
    confirmDialog.value.show = false;
    const ymHint = needsYearMonth(type)
      ? ` (${formatYearMonthDisplay(selectedYearMonth.value)} · ${selectedYearMonth.value})`
      : '';
    showMessage(`${getTypeLabel(type)} 요청이 등록되었습니다.${ymHint}`);
    loadHistory();
    startPolling();
  } catch (error: any) {
    const msg = formatRequestError(
      error.response?.data?.message || error.message || '요청 등록에 실패했습니다.'
    );
    showMessage(msg, 'error');
  } finally {
    loadingMap.value[type] = false;
  }
};

const cancelRequest = async (item: any) => {
  try {
    await api.post(`/macro/${item.cmq_idx}/cancel`);
    showMessage('요청이 취소되었습니다.');
    loadHistory();
  } catch (error: any) {
    const msg = error.response?.data?.message || '요청 취소에 실패했습니다.';
    showMessage(msg, 'error');
  }
};

// 결과 보기 모달 열기
const showResultModal = async (item: any) => {
  // params에서 yearMonth, bizGubun 추출
  let yearMonth = '';
  let reqBizGubun = '';
  try {
    const params = typeof item.cmq_params === 'string' 
      ? JSON.parse(item.cmq_params) 
      : item.cmq_params;
    yearMonth = params?.yearMonth || '';
    reqBizGubun = params?.bizGubun || '';
  } catch {
    yearMonth = '';
    reqBizGubun = '';
  }

  const normalizedType = normalizeLtcsType(item.cmq_type);

  resultModal.value = {
    show: true,
    status: item.cmq_status,
    type: normalizedType,
    requestedAt: item.cmq_requested_at,
    completedAt: item.cmq_completed_at,
    result: item.cmq_result,
    errorMessage: formatRequestError(item.cmq_error_message || ''),
    yearMonth,
    bizGubun: reqBizGubun // ★ 요청 시 사업구분
  };

  // 성공한 요청인 경우 데이터 로드 (8개 타입 모두 지원)
  const supportedTypes = Object.keys(TYPE_LABELS);
  if (item.cmq_status === 'completed' && supportedTypes.includes(normalizedType)) {
    currentPage.value = 1;
    scheduleSearch.value = '';
    const { defaultSortKey } = getEndpointConfig(normalizedType);
    sortBy.value = [{ key: defaultSortKey, order: 'asc' as const }];
    await loadScheduleData();
  }
};

// 결과 모달 닫기
const closeResultModal = () => {
  resultModal.value.show = false;
  duplicateDialogShow.value = false;
  duplicateGroups.value = [];
  scheduleData.value = {
    items: [],
    total: 0,
    summary: {
      totalCount: 0,
      caregiverCount: 0,
      gradeCount: 0,
      totalFee: 0,
      recipientCount: 0,
      dateCount: 0,
      duplicateCount: 0,
      duplicateGroupCount: 0,
      portalRawCount: 0,
      paidCount: 0,
      requestedCount: 0,
      statusPairCount: 0,
    }
  };
  scheduleSearch.value = '';
  currentPage.value = 1;
};

// 타입별 API 엔드포인트 및 기본 정렬키 매핑
const getEndpointConfig = (type: string) => {
  const normalized = normalizeLtcsType(type);
  const config: Record<string, { endpoint: string; defaultSortKey: string; needsYearMonth: boolean }> = {
    caion_ltcs_recipient_info: { endpoint: '/ltcs/recipient-info', defaultSortKey: 'clsr_recipient_name', needsYearMonth: false },
    caion_ltcs_emp_info: { endpoint: '/ltcs/caregiver-info', defaultSortKey: 'clci_name', needsYearMonth: false },
    caion_ltcs_schedule_plan: { endpoint: '/ltcs/caregiver-plan', defaultSortKey: 'clsp_date', needsYearMonth: true },
    caion_ltcs_rfid: { endpoint: '/ltcs/rfid', defaultSortKey: 'clrf_start_dt', needsYearMonth: true },
    caion_ltcs_claim: { endpoint: '/ltcs/claim', defaultSortKey: 'clc_date', needsYearMonth: true },
    caion_ltcs_claim_detail: { endpoint: '/ltcs/claim-detail', defaultSortKey: 'clcd_date', needsYearMonth: true },
    caion_ltcs_labor_cost: { endpoint: '/ltcs/labor-cost', defaultSortKey: 'yearMonth', needsYearMonth: true }
  };
  return config[normalized] || { endpoint: '/ltcs/recipient-info', defaultSortKey: 'clsr_recipient_name', needsYearMonth: false };
};

const loadScheduleData = async () => {
  if (!memberCode.value) return;

  const { endpoint, defaultSortKey, needsYearMonth: typeNeedsYm } = getEndpointConfig(resultModal.value.type);
  // ★ 요청 시 저장된 bizGubun 사용 (현재 화면의 bizGubun 대신)
  const modalBizGubun = resultModal.value.bizGubun || bizGubun.value;

  if (typeNeedsYm && (!modalBizGubun || !resultModal.value.yearMonth)) return;

  scheduleLoading.value = true;
  try {
    if (resultModal.value.type === 'caion_ltcs_labor_cost') {
      try {
        const res = await api.get(`${endpoint}/${memberCode.value}/${resultModal.value.yearMonth}`, {
          params: { bizGubun: modalBizGubun }
        });

        if (res.data.data) {
          scheduleData.value.items = [res.data.data];
          scheduleData.value.total = 1;
          scheduleData.value.summary = {
            totalCount: 1,
            caregiverCount: 0,
            gradeCount: 0,
            totalFee: (res.data.data.totalCost || 0) + (res.data.data.totalAdd || 0),
            recipientCount: 0,
            dateCount: 0,
            duplicateCount: 0,
            duplicateGroupCount: 0,
          };
          await loadLaborCostDuplicateStats(modalBizGubun);
        } else {
          scheduleData.value.items = [];
          scheduleData.value.total = 0;
          scheduleData.value.summary = {
            totalCount: 0,
            caregiverCount: 0,
            gradeCount: 0,
            totalFee: 0,
            recipientCount: 0,
            dateCount: 0,
            duplicateCount: 0,
            duplicateGroupCount: 0,
          };
        }
      } catch (error) {
        console.error('인건비지출내역 조회 실패:', error);
        scheduleData.value.items = [];
        scheduleData.value.total = 0;
      } finally {
        scheduleLoading.value = false;
      }
      return;
    }

    const sortKey = sortBy.value[0]?.key || defaultSortKey;
    const sortOrder = sortBy.value[0]?.order || 'asc';

    const queryParams: Record<string, any> = {
      mCode: memberCode.value,
      search: scheduleSearch.value,
      sortBy: sortKey,
      sortOrder,
      limit: itemsPerPage.value,
      offset: (currentPage.value - 1) * itemsPerPage.value
    };
    if (modalBizGubun) queryParams.bizGubun = modalBizGubun;
    if (resultModal.value.yearMonth) queryParams.yearMonth = resultModal.value.yearMonth;

    const res = await api.get(endpoint, { params: queryParams });

    scheduleData.value.items = res.data.data || [];
    scheduleData.value.total = res.data.total || 0;

    if (!scheduleSearch.value) {
      const summaryRes = await api.get(`${endpoint}/summary`, {
        params: {
          mCode: memberCode.value,
          ...(modalBizGubun ? { bizGubun: modalBizGubun } : {}),
          ...(resultModal.value.yearMonth ? { yearMonth: resultModal.value.yearMonth } : {})
        }
      });
      scheduleData.value.summary = summaryRes.data.data || {};
    }
  } catch (error) {
    console.error('데이터 조회 실패:', error);
    showMessage('데이터 조회에 실패했습니다.', 'error');
  } finally {
    scheduleLoading.value = false;
  }
};

const loadLaborCostDuplicateStats = async (modalBizGubun: string) => {
  const endpoint = getLtcsDuplicatesEndpoint('caion_ltcs_labor_cost');
  if (!endpoint || !memberCode.value) return;
  try {
    const res = await api.get(endpoint, {
      params: {
        mCode: memberCode.value,
        bizGubun: modalBizGubun,
        yearMonth: resultModal.value.yearMonth,
      },
    });
    const data = res.data?.data || {};
    scheduleData.value.summary.duplicateCount = data.duplicateCount || 0;
    scheduleData.value.summary.duplicateGroupCount = data.duplicateGroupCount || 0;
  } catch (error) {
    console.error('인건비 중복 통계 조회 실패:', error);
  }
};

const loadDuplicateGroups = async () => {
  const endpoint = getLtcsDuplicatesEndpoint(resultModal.value.type);
  if (!endpoint || !memberCode.value) return;

  const modalBizGubun = resultModal.value.bizGubun || bizGubun.value;
  duplicateLoading.value = true;
  try {
    const res = await api.get(endpoint, {
      params: {
        mCode: memberCode.value,
        ...(modalBizGubun ? { bizGubun: modalBizGubun } : {}),
        ...(resultModal.value.yearMonth ? { yearMonth: resultModal.value.yearMonth } : {}),
        groupLimit: 50,
      },
    });
    const data = res.data?.data || {};
    duplicateGroups.value = data.groups || [];
    if (typeof data.duplicateCount === 'number') {
      scheduleData.value.summary.duplicateCount = data.duplicateCount;
    }
    if (typeof data.duplicateGroupCount === 'number') {
      scheduleData.value.summary.duplicateGroupCount = data.duplicateGroupCount;
    }
  } catch (error: any) {
    const msg = error.response?.data?.message || error.message || '중복 데이터 조회에 실패했습니다.';
    showMessage(msg, 'error');
    console.error('중복 데이터 조회 실패:', error);
  } finally {
    duplicateLoading.value = false;
  }
};

const openDuplicateDialog = async () => {
  if (duplicateCount.value <= 0) return;
  duplicateDialogShow.value = true;
  duplicateGroups.value = [];
  await loadDuplicateGroups();
};

// 검색 실행
const searchScheduleData = () => {
  currentPage.value = 1;
  loadScheduleData();
};

// 검색어 초기화
const clearSearch = () => {
  scheduleSearch.value = '';
  currentPage.value = 1;
  loadScheduleData();
};

// 정렬 변경
const onSortChange = (newSort: any) => {
  sortBy.value = newSort;
  loadScheduleData();
};

// 페이지 변경
const onPageChange = () => {
  loadScheduleData();
};

// 페이지당 표시 건수 변경
const onItemsPerPageChange = () => {
  currentPage.value = 1;
  loadScheduleData();
};

// 연월 포맷팅 (YYYYMM → YYYY년 MM월)
const formatYearMonth = (ym: string) => {
  if (!ym || ym.length !== 6) return ym || '';
  const year = ym.substring(0, 4);
  const month = ym.substring(4, 6);
  return `${year}년 ${parseInt(month)}월`;
};

// 날짜 포맷팅 (월/일)
const formatDate = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

// 날짜 포맷팅 (연-월-일)
const formatDateFull = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 시간 범위 포맷팅
const formatTimeRange = (start: string, end: string) => {
  if (!start || !end) return '-';
  return `${start.substring(0, 5)} ~ ${end.substring(0, 5)}`;
};

// 시간 포맷팅 (단일)
const formatTime = (time: string) => {
  if (!time) return '-';
  return time.substring(0, 5);
};

const formatMoney = (val: number | string | null | undefined) => {
  if (val === null || val === undefined || val === '') return '-';
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return `${n.toLocaleString()}원`;
};

const formatRate = (val: number | string | null | undefined) => {
  if (val === null || val === undefined || val === '') return '-';
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return `${n}%`;
};

const formatYn = (val: string | null | undefined) => {
  if (!val || val === 'N') return '-';
  if (val === 'Y') return 'Y';
  return val;
};

// 서비스 타입 색상
const getServiceTypeColor = (type: string) => {
  switch (type) {
    case '방문요양': return 'success';
    case '방문목욕': return 'info';
    case '방문간호': return 'error';
    case '주야간보호': return 'warning';
    default: return 'grey';
  }
};

// 청구 상태 색상
const getClaimStatusColor = (status: string) => {
  switch (status) {
    case '지급':
    case '승인': return 'success';
    case '반려': return 'error';
    case '심사중': return 'warning';
    case '청구의뢰':
    case '청구완료': return 'info';
    default: return 'grey';
  }
};

const loadWorkers = async () => {
  workersLoading.value = true;
  try {
    const res = await api.get('/macro/workers');
    workers.value = res.data.data || [];
  } catch (error) {
    console.error('워커 목록 조회 실패:', error);
  } finally {
    workersLoading.value = false;
  }
};

const loadHistory = async () => {
  if (!memberCode.value) return;

  historyLoading.value = true;
  try {
    const res = await api.get(`/macro/member/${memberCode.value}`, {
      params: { limit: 50 }
    });
    const next = res.data.data || [];
    if (hasNewCertExpiredFailure(historyItems.value, next)) {
      showMessage(CERT_EXPIRED_CRAWL_MESSAGE, 'error');
    }
    historyItems.value = next;
  } catch (error) {
    console.error('요청 내역 조회 실패:', error);
  } finally {
    historyLoading.value = false;
  }
};

// 자동 갱신 폴링
const startPolling = () => {
  if (pollingInterval) return; // 이미 실행 중이면 무시
  
  pollingInterval = setInterval(() => {
    if (hasActiveRequest.value) {
      loadHistory();
    } else {
      stopPolling(); // 활성 요청이 없으면 폴링 중지
    }
  }, 3000); // 3초마다 갱신
};

const stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
};

// 활성 요청 상태 감시
watch(hasActiveRequest, (hasActive) => {
  if (hasActive) {
    startPolling();
  } else {
    stopPolling();
  }
});

// memberCode가 로드되면 히스토리 조회
watch(() => memberCode.value, (newVal) => {
  if (newVal) {
    loadHistory();
  }
}, { immediate: true });

watch(
  sharedYearMonth,
  (ym) => {
    if (ym && selectedYearMonth.value !== ym) {
      selectedYearMonth.value = ym;
    }
  },
  { immediate: true }
);

watch(selectedYearMonth, (ym) => {
  if (!ym || ym.length < 6) return;
  const y = parseInt(ym.slice(0, 4), 10);
  const m = parseInt(ym.slice(4, 6), 10);
  if (y >= 2000 && m >= 1 && m <= 12 && sharedYearMonth.value !== ym) {
    setYearMonth(y, m);
  }
});

onMounted(() => {
  if (!selectedYearMonth.value && yearMonthOptions.value.length > 0) {
    selectedYearMonth.value = yearMonthOptions.value[0].value;
  }
  loadWorkers();
  workerPollingInterval = setInterval(loadWorkers, 15000);
});

onUnmounted(() => {
  stopPolling();
  if (workerPollingInterval) {
    clearInterval(workerPollingInterval);
    workerPollingInterval = null;
  }
});
</script>
